import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';

const BRISBANE_TZ = 'Australia/Brisbane';

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 'var(--spacing-4)',
  flexWrap: 'wrap',
  gap: 'var(--spacing-3)',
};

const btnStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
  fontSize: 'var(--font-size-sm)',
  cursor: 'pointer',
  fontWeight: '500',
};

const btnAccent: React.CSSProperties = {
  ...btnStyle,
  backgroundColor: 'var(--color-accent)',
  border: '1px solid var(--color-accent)',
  color: '#1e1e1e',
  fontWeight: '600',
};

const selectStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
  fontSize: 'var(--font-size-sm)',
};

const inputStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  backgroundColor: 'var(--color-surface-elevated)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
  fontSize: 'var(--font-size-base)',
  width: '100%',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  fontWeight: '600',
  marginBottom: 'var(--spacing-1)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--color-text-muted)',
};

const cellButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  padding: '6px 10px',
  borderRadius: '999px',
  border: '1px dashed rgba(30, 30, 30, 0.14)',
  backgroundColor: '#fff',
  color: 'var(--color-text-muted)',
  fontSize: '12px',
  fontWeight: 600,
};

const locationPalette = [
  {
    card: 'rgba(59, 155, 120, 0.14)',
    border: '#3b9b78',
    text: '#245f49',
    muted: '#437463',
  },
  {
    card: 'rgba(217, 178, 86, 0.16)',
    border: '#c79b2d',
    text: '#5b4520',
    muted: '#7f6430',
  },
  {
    card: 'rgba(65, 115, 223, 0.12)',
    border: '#4173df',
    text: '#264a91',
    muted: '#5374b5',
  },
  {
    card: 'rgba(203, 86, 140, 0.14)',
    border: '#cb568c',
    text: '#7f3154',
    muted: '#9e5a76',
  },
];

type RosterPreset = 'TODAY' | 'THIS_WEEK' | 'LAST_WEEK';

type ShiftFormState = {
  id?: number;
  barber_id: number;
  location_id: number;
  date: string;
  startTime: string;
  endTime: string;
};

type BreakFormState = {
  id?: number;
  barber_id: number;
  date: string;
  startTime: string;
  endTime: string;
};

function getTodayBrisbane(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: BRISBANE_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

function addDays(dateStr: string, days: number): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function startOfWeek(dateStr: string): string {
  const date = new Date(`${dateStr}T12:00:00`);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  return date.toISOString().slice(0, 10);
}

function formatDateDisplay(dateStr: string): string {
  const d = new Date(`${dateStr}T12:00:00`);
  return d.toLocaleDateString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatDayHeader(dateStr: string): { weekday: string; day: string } {
  const d = new Date(`${dateStr}T12:00:00`);
  return {
    weekday: d.toLocaleDateString('en-AU', { weekday: 'short' }),
    day: d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }),
  };
}

function brisbaneDateToISO(dateStr: string, timeStr: string): string {
  // Build the UTC instant that corresponds to {dateStr, timeStr} interpreted as
  // Brisbane wall-clock time, INDEPENDENT of the browser's own timezone.
  //
  // Previous implementation used `en-AU` Intl formatter without `hour12: false`,
  // so for any browser TZ where Brisbane noon falls in PM (UTC, WA, Bali, etc.)
  // the formatter returned `hour: "02"` with `dayPeriod: "pm"` — parsed as
  // 24-hour "02:00" the offset was wrong by 12h and rosters landed on the
  // wrong day. Use `en-GB` + `hour12: false` to lock the formatter to 24-hour.
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hour, minute] = timeStr.split(':').map(Number);
  const testDate = new Date(year, month - 1, day, 12, 0, 0);
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: BRISBANE_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(testDate);
  const partMap: Record<string, string> = {};
  parts.forEach((part) => {
    if (part.type !== 'literal') partMap[part.type] = part.value;
  });
  // en-GB + hour12:false can return "24" for midnight on some engines —
  // normalise to "00" so the Date constructor parses it.
  const safeHour = partMap.hour === '24' ? '00' : partMap.hour;
  const localDate = new Date(
    `${partMap.year}-${partMap.month}-${partMap.day}T${safeHour}:${partMap.minute}:${partMap.second}`,
  );
  const offsetMs = testDate.getTime() - localDate.getTime();
  const targetLocal = new Date(year, month - 1, day, hour, minute, 0);
  return new Date(targetLocal.getTime() + offsetMs).toISOString();
}

function toDateInputValue(isoString: string) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: BRISBANE_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(isoString));
}

function toTimeInputValue(isoString: string) {
  return new Date(isoString).toLocaleTimeString('en-AU', {
    timeZone: BRISBANE_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function formatDayNav(dateStr: string): string {
  const { weekday, day } = formatDayHeader(dateStr);
  return `${weekday} ${day}`;
}

function formatBrisbaneTime(isoString: string): string {
  return new Date(isoString)
    .toLocaleTimeString('en-AU', {
      timeZone: BRISBANE_TZ,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    .replace(' am', 'a')
    .replace(' pm', 'p');
}

function getRangeForPreset(preset: RosterPreset, anchorDate: string) {
  if (preset === 'TODAY') {
    return { startDate: anchorDate, endDate: anchorDate };
  }
  if (preset === 'LAST_WEEK') {
    const start = addDays(startOfWeek(anchorDate), -7);
    return { startDate: start, endDate: addDays(start, 6) };
  }
  const start = startOfWeek(anchorDate);
  return { startDate: start, endDate: addDays(start, 6) };
}

function getDaysInRange(startDate: string, endDate: string) {
  const days: string[] = [];
  let current = startDate;
  while (current <= endDate) {
    days.push(current);
    current = addDays(current, 1);
  }
  return days;
}

function getPalette(locationId: number, locationIds: number[]) {
  const index = Math.max(0, locationIds.indexOf(locationId));
  return locationPalette[index % locationPalette.length];
}

// Cells are scoped to (day, barber, location) — NOT just (day, barber).
// A multi-location barber (Kiana → Nundah + Mitchelton) has her row rendered
// under EACH served-location's group, and each render must only show the
// shifts that belong to *that* location. Keying by barber alone would make
// her Mon-Nundah shift appear in both the Nundah and Mitchelton rows.
function getCellKey(day: string, barberId: number, locationId: number) {
  return `${day}:${barberId}:${locationId}`;
}

function ModalShell({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 'var(--spacing-6)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '900px',
          backgroundColor: '#fff',
          borderRadius: 'var(--radius-lg)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 'var(--spacing-4) var(--spacing-5)',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <h2 style={{ margin: 0 }}>{title}</h2>
          <button onClick={onClose} style={{ ...btnStyle, padding: '6px 10px' }}>
            Close
          </button>
        </div>
        <div style={{ padding: 'var(--spacing-5)' }}>{children}</div>
      </div>
    </div>
  );
}

export function Rosters() {
  const queryClient = useQueryClient();
  const [preset, setPreset] = useState<RosterPreset>('THIS_WEEK');
  const [anchorDate, setAnchorDate] = useState(getTodayBrisbane);
  const [locationId, setLocationId] = useState<number | undefined>(undefined);
  const [barberId, setBarberId] = useState<number | undefined>(undefined);
  const [shiftForm, setShiftForm] = useState<ShiftFormState | null>(null);
  const [breakForm, setBreakForm] = useState<BreakFormState | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [mobileDayIndex, setMobileDayIndex] = useState(0);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copyDialogSelection, setCopyDialogSelection] = useState<Set<number>>(new Set());
  // How many weeks back to copy the source roster from. Default 1
  // ("last week") but the admin can pick 2/3/4 to preserve fortnightly
  // patterns (Lee's alternating Thu Nundah rotation is a 2-week cycle).
  const [copyWeeksBack, setCopyWeeksBack] = useState<number>(1);

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const range = useMemo(() => getRangeForPreset(preset, anchorDate), [preset, anchorDate]);
  const days = useMemo(
    () => getDaysInRange(range.startDate, range.endDate),
    [range.startDate, range.endDate],
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const todayIdx = days.indexOf(getTodayBrisbane());
    setMobileDayIndex(todayIdx >= 0 ? todayIdx : 0);
  }, [days]);

  const { data: locations = [] } = useQuery({
    queryKey: ['locations'],
    queryFn: api.getLocations,
  });
  const { data: allBarbers = [] } = useQuery({
    queryKey: ['barbers', locationId],
    queryFn: () => api.getBarbers(locationId),
  });
  const { data: openingHours = [] } = useQuery({
    queryKey: ['opening-hours'],
    queryFn: () => api.getOpeningHours(),
  });

  const rosterParams = useMemo(() => {
    const params: Record<string, string> = {
      startDate: `${range.startDate}T00:00:00`,
      endDate: `${addDays(range.endDate, 1)}T00:00:00`,
    };
    if (locationId) params.locationId = String(locationId);
    if (barberId) params.barberId = String(barberId);
    return params;
  }, [range.startDate, range.endDate, locationId, barberId]);

  const { data: shifts = [], isLoading: loadingShifts } = useQuery({
    queryKey: ['rosters', rosterParams],
    queryFn: () => api.getRosters(rosterParams),
  });
  const { data: rawBreaks = [], isLoading: loadingBreaks } = useQuery({
    queryKey: ['breaks', rosterParams],
    queryFn: () => api.getBreaks(rosterParams),
  });
  // The Rosters view is only for RECURRING breaks (rest time). Catch-up
  // rows (`type='catchup'`) are day-specific, live on the Calendar, and
  // must never show up in the roster grid — otherwise a catchup created
  // in the Calendar UI (or one that pre-dates the copy-week fix) shows
  // as a phantom "Break" alongside the real break and looks like a
  // duplicate.
  const breaks = useMemo(
    () => (rawBreaks as any[]).filter((b) => (b.type ?? 'break') === 'break'),
    [rawBreaks],
  );

  // A barber's served locations live on `barber.locations[{id,name}]`
  // (post-2026-06 multi-location refactor). `barber.location_id` is
  // their primary/home location only — it's NOT a filter key here
  // because a multi-location barber (Kiana → Nundah + Mitchelton)
  // would otherwise vanish from any location other than her home.
  const barberServesLocation = (barber: any, locId: number): boolean => {
    if (Array.isArray(barber.locations) && barber.locations.length > 0) {
      return barber.locations.some((l: any) => Number(l.id) === Number(locId));
    }
    return Number(barber.location_id) === Number(locId);
  };

  const filteredBarbers = useMemo(() => {
    if (!locationId) return allBarbers;
    return allBarbers.filter((barber: any) => barberServesLocation(barber, locationId));
  }, [allBarbers, locationId]);

  const locationNameById = useMemo(
    () => new Map(locations.map((location: any) => [location.id, location.name])),
    [locations],
  );

  const shiftsByCell = useMemo(() => {
    const map = new Map<string, any[]>();
    for (const shift of shifts) {
      const key = getCellKey(
        toDateInputValue(shift.start_time),
        shift.barber_id,
        shift.location_id,
      );
      const cell = map.get(key) || [];
      cell.push(shift);
      map.set(key, cell);
    }
    for (const cell of map.values()) {
      cell.sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
    }
    return map;
  }, [shifts]);

  // `barber_break` has no `location_id` column (legacy schema), but multi-
  // location barbers need breaks attributed to the right location so they
  // render in the right group only. Derive each break's location from the
  // shift it overlaps with; if no overlap, fall back to the barber's home.
  const breakLocationById = useMemo(() => {
    const map = new Map<number, number>();
    const homeByBarber = new Map<number, number>();
    for (const barber of allBarbers) {
      homeByBarber.set(barber.id, Number(barber.location_id));
    }
    for (const item of breaks) {
      const breakStart = new Date(item.start_time).getTime();
      const breakEnd = new Date(item.end_time).getTime();
      const overlap = shifts.find((shift: any) => {
        if (shift.barber_id !== item.barber_id) return false;
        const shiftStart = new Date(shift.start_time).getTime();
        const shiftEnd = new Date(shift.end_time).getTime();
        return breakStart < shiftEnd && breakEnd > shiftStart;
      });
      const locId = overlap ? Number(overlap.location_id) : (homeByBarber.get(item.barber_id) ?? 0);
      map.set(item.id, locId);
    }
    return map;
  }, [breaks, shifts, allBarbers]);

  const breaksByCell = useMemo(() => {
    const map = new Map<string, any[]>();
    for (const item of breaks) {
      const locId = breakLocationById.get(item.id);
      if (!locId) continue;
      const key = getCellKey(toDateInputValue(item.start_time), item.barber_id, locId);
      const cell = map.get(key) || [];
      cell.push(item);
      map.set(key, cell);
    }
    for (const cell of map.values()) {
      cell.sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
    }
    return map;
  }, [breaks, breakLocationById]);

  const groupedBarbers = useMemo(() => {
    const groups = new Map<number, any[]>();
    for (const barber of filteredBarbers) {
      // In single-location view, group everyone under the page filter.
      // In all-locations view, a multi-location barber appears in
      // EACH of her served-locations groups (so Kiana shows under
      // both Nundah and Mitchelton, not just her primary home).
      const servedLocs: number[] = locationId
        ? [locationId]
        : Array.isArray(barber.locations) && barber.locations.length > 0
          ? barber.locations.map((l: any) => Number(l.id))
          : [Number(barber.location_id)];
      // Only shops that are still trading get a roster section. `locations`
      // holds the enabled ones, so anything missing from it is closed —
      // without this, Lee and Loren (who still serve Nundah as well as
      // Mitchelton) produced a second, nameless group that rendered under
      // the fallback label "Location".
      const targetLocs = servedLocs.filter((id) => locationNameById.has(id));
      for (const groupLocationId of targetLocs) {
        const current = groups.get(groupLocationId) || [];
        current.push(barber);
        groups.set(groupLocationId, current);
      }
    }
    return Array.from(groups.entries())
      .map(([groupLocationId, barbers]) => ({
        locationId: groupLocationId,
        locationName: locationNameById.get(groupLocationId)!,
        barbers: [...barbers].sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.locationName.localeCompare(b.locationName));
  }, [filteredBarbers, locationNameById, locationId]);

  // Filtering closed shops out of the grid must not quietly lose anyone.
  // A barber attached ONLY to a closed shop has nowhere left to be
  // rostered, so they get named rather than silently dropped.
  const barbersWithNoTradingShop = useMemo(() => {
    if (locationId) return [];
    return filteredBarbers
      .filter((barber: any) => {
        const served: number[] =
          Array.isArray(barber.locations) && barber.locations.length > 0
            ? barber.locations.map((l: any) => Number(l.id))
            : [Number(barber.location_id)];
        return !served.some((id) => locationNameById.has(id));
      })
      .map((barber: any) => barber.name)
      .sort((a: string, b: string) => a.localeCompare(b));
  }, [filteredBarbers, locationNameById, locationId]);

  // Derived from groupedBarbers so it picks up multi-location expansion.
  const locationIdsInView = useMemo(
    () => Array.from(new Set(groupedBarbers.map((g) => g.locationId))).sort((a, b) => a - b),
    [groupedBarbers],
  );

  const createShiftMutation = useMutation({
    mutationFn: api.createRoster,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rosters'] });
      setShiftForm(null);
      showToast('Shift created', 'success');
    },
    onError: (err: any) => showToast(err?.message || 'Failed to create shift', 'error'),
  });
  const updateShiftMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => api.updateRoster(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rosters'] });
      setShiftForm(null);
      showToast('Shift updated', 'success');
    },
    onError: (err: any) => showToast(err?.message || 'Failed to update shift', 'error'),
  });
  const deleteShiftMutation = useMutation({
    mutationFn: api.deleteRoster,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rosters'] });
      setShiftForm(null);
      showToast('Shift deleted', 'success');
    },
    onError: (err: any) => showToast(err?.message || 'Failed to delete shift', 'error'),
  });
  const createBreakMutation = useMutation({
    mutationFn: api.createBreak,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['breaks'] });
      setBreakForm(null);
      showToast('Break created', 'success');
    },
    onError: (err: any) => showToast(err?.message || 'Failed to create break', 'error'),
  });
  const updateBreakMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => api.updateBreak(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['breaks'] });
      setBreakForm(null);
      showToast('Break updated', 'success');
    },
    onError: (err: any) => showToast(err?.message || 'Failed to update break', 'error'),
  });
  const deleteBreakMutation = useMutation({
    mutationFn: api.deleteBreak,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['breaks'] });
      setBreakForm(null);
      showToast('Break deleted', 'success');
    },
    onError: (err: any) => showToast(err?.message || 'Failed to delete break', 'error'),
  });
  const copyWeekMutation = useMutation({
    mutationFn: async (
      targets: {
        barberId: number;
        locationId: number;
        weekStart: string;
        handleBreaks: boolean;
        sourceWeekStart?: string;
      }[],
    ) => Promise.all(targets.map((target) => api.copyRosterWeek(target))),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rosters'] });
      queryClient.invalidateQueries({ queryKey: ['breaks'] });
      showToast('Week copied', 'success');
    },
    onError: (err: any) => showToast(err?.message || 'Failed to copy week', 'error'),
  });

  const { data: coverage } = useQuery({
    queryKey: ['roster-coverage'],
    queryFn: api.getRosterCoverage,
  });

  const autoExtendMutation = useMutation({
    mutationFn: api.autoExtendRosters,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rosters'] });
      queryClient.invalidateQueries({ queryKey: ['breaks'] });
      queryClient.invalidateQueries({ queryKey: ['roster-coverage'] });
      showToast('Rosters extended', 'success');
    },
    onError: (err: any) => showToast(err?.message || 'Failed to extend rosters', 'error'),
  });

  // `groupLocationId` is the location of the grid row the user clicked from.
  // It's the source of truth — without it a multi-location barber's "+ Shift"
  // click would silently default to her home location (post-merge: Mitchelton),
  // so a click under the Nundah row would create a Mitchelton shift.
  const openNewShift = (day: string, preferredBarberId?: number, groupLocationId?: number) => {
    const defaultBarber = preferredBarberId
      ? filteredBarbers.find((barber: any) => barber.id === preferredBarberId)
      : filteredBarbers[0];
    const defaultLocation =
      groupLocationId || locationId || defaultBarber?.location_id || locations[0]?.id;
    if (!defaultBarber || !defaultLocation) return;
    setShiftForm({
      barber_id: defaultBarber.id,
      location_id: defaultLocation,
      date: day,
      startTime: '09:00',
      endTime: '17:00',
    });
  };

  const openEditShift = (shift: any) => {
    setShiftForm({
      id: shift.id,
      barber_id: shift.barber_id,
      location_id: shift.location_id,
      date: toDateInputValue(shift.start_time),
      startTime: toTimeInputValue(shift.start_time),
      endTime: toTimeInputValue(shift.end_time),
    });
  };

  const openNewBreak = (day: string, preferredBarberId?: number) => {
    const defaultBarber = preferredBarberId
      ? filteredBarbers.find((barber: any) => barber.id === preferredBarberId)
      : filteredBarbers[0];
    if (!defaultBarber) return;
    setBreakForm({
      barber_id: defaultBarber.id,
      date: day,
      startTime: '12:00',
      endTime: '12:30',
    });
  };

  const openEditBreak = (item: any) => {
    setBreakForm({
      id: item.id,
      barber_id: item.barber_id,
      date: toDateInputValue(item.start_time),
      startTime: toTimeInputValue(item.start_time),
      endTime: toTimeInputValue(item.end_time),
    });
  };

  const saveShift = () => {
    if (!shiftForm) return;
    const payload = {
      barber_id: shiftForm.barber_id,
      location_id: shiftForm.location_id,
      start_time: brisbaneDateToISO(shiftForm.date, shiftForm.startTime),
      end_time: brisbaneDateToISO(shiftForm.date, shiftForm.endTime),
    };
    if (shiftForm.id) {
      updateShiftMutation.mutate({ id: shiftForm.id, data: payload });
    } else {
      createShiftMutation.mutate(payload);
    }
  };

  const saveBreak = () => {
    if (!breakForm) return;
    const payload = {
      barber_id: breakForm.barber_id,
      start_time: brisbaneDateToISO(breakForm.date, breakForm.startTime),
      end_time: brisbaneDateToISO(breakForm.date, breakForm.endTime),
    };
    if (breakForm.id) {
      updateBreakMutation.mutate({ id: breakForm.id, data: payload });
    } else {
      createBreakMutation.mutate(payload);
    }
  };

  return (
    <div>
      <div style={headerStyle}>
        <h1
          style={{
            fontSize: 'var(--font-size-2xl)',
            fontFamily: 'var(--font-family-heading)',
            margin: 0,
          }}
        >
          Rosters
        </h1>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
            flexWrap: 'wrap',
          }}
        >
          <button
            onClick={() =>
              setAnchorDate((current) => addDays(current, preset === 'TODAY' ? -1 : -7))
            }
            style={btnStyle}
            title="Previous"
          >
            &lsaquo;
          </button>
          <button
            onClick={() => {
              setPreset('TODAY');
              setAnchorDate(getTodayBrisbane());
            }}
            style={btnStyle}
          >
            Today
          </button>
          <button
            onClick={() => setPreset('THIS_WEEK')}
            style={preset === 'THIS_WEEK' ? btnAccent : btnStyle}
          >
            This Week
          </button>
          <button
            onClick={() => setPreset('LAST_WEEK')}
            style={preset === 'LAST_WEEK' ? btnAccent : btnStyle}
          >
            Last Week
          </button>
          <input
            type="date"
            value={anchorDate}
            onChange={(e) => setAnchorDate(e.target.value)}
            style={{ ...selectStyle, minWidth: '150px' }}
          />
          <button
            onClick={() => setAnchorDate((current) => addDays(current, preset === 'TODAY' ? 1 : 7))}
            style={btnStyle}
            title="Next"
          >
            &rsaquo;
          </button>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-3)',
            flexWrap: 'wrap',
          }}
        >
          <select
            value={locationId || ''}
            onChange={(e) => {
              const nextLocationId = e.target.value ? Number(e.target.value) : undefined;
              setLocationId(nextLocationId);
              setBarberId(undefined);
            }}
            style={selectStyle}
          >
            <option value="">All Locations</option>
            {locations.map((location: any) => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
          </select>

          <select
            value={barberId || ''}
            onChange={(e) => setBarberId(e.target.value ? Number(e.target.value) : undefined)}
            style={selectStyle}
          >
            <option value="">All Barbers</option>
            {filteredBarbers.map((barber: any) => (
              <option key={barber.id} value={barber.id}>
                {barber.name}
              </option>
            ))}
          </select>

          <button onClick={() => openNewBreak(range.startDate, barberId)} style={btnStyle}>
            + Break
          </button>
          <button onClick={() => openNewShift(range.startDate, barberId)} style={btnAccent}>
            + Shift
          </button>
          <button
            onClick={() => {
              // Open the selective-copy dialog. Defaults selection to the
              // top-level barber filter if one is set, otherwise leaves
              // it empty so the admin has to pick — copying every barber
              // in one click was too easy to trigger by accident.
              setCopyDialogSelection(barberId ? new Set([barberId]) : new Set<number>());
              setCopyDialogOpen(true);
            }}
            disabled={copyWeekMutation.isPending || filteredBarbers.length === 0}
            style={btnStyle}
          >
            {copyWeekMutation.isPending ? 'Copying...' : 'Copy a past week'}
          </button>
        </div>
      </div>

      {/* Roster Coverage Warning */}
      {coverage?.needsExtension && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--spacing-3)',
            padding: 'var(--spacing-3) var(--spacing-4)',
            backgroundColor: 'rgba(217, 178, 86, 0.12)',
            border: '1px solid rgba(217, 178, 86, 0.4)',
            borderRadius: 'var(--radius-default)',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          <div>
            <strong style={{ color: '#8a6d1b' }}>⚠ Roster coverage gap</strong>
            <p
              style={{
                margin: '4px 0 0',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-muted)',
              }}
            >
              Rosters currently extend to <strong>{coverage.lastDate || 'none'}</strong>. The system
              requires coverage until <strong>{coverage.targetDate}</strong> (4 months ahead).
              {coverage.weeksRemaining > 0 &&
                ` ${coverage.weeksRemaining} week(s) of shifts remain.`}
            </p>
          </div>
          <button
            onClick={() => autoExtendMutation.mutate()}
            disabled={autoExtendMutation.isPending}
            style={{
              ...btnAccent,
              whiteSpace: 'nowrap',
              opacity: autoExtendMutation.isPending ? 0.7 : 1,
            }}
          >
            {autoExtendMutation.isPending ? 'Extending...' : '🔄 Auto-extend 4 Months'}
          </button>
        </div>
      )}

      {autoExtendMutation.isSuccess && (
        <div
          style={{
            padding: 'var(--spacing-3) var(--spacing-4)',
            backgroundColor: 'rgba(40, 167, 69, 0.1)',
            border: '1px solid rgba(40, 167, 69, 0.3)',
            borderRadius: 'var(--radius-default)',
            marginBottom: 'var(--spacing-4)',
            fontSize: 'var(--font-size-sm)',
            color: '#1e7e34',
          }}
        >
          ✓ Rosters extended by {(autoExtendMutation.data as any)?.weeksAdded} week(s). Coverage now
          extends to {(autoExtendMutation.data as any)?.lastDateAfter}.
        </div>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--spacing-3)',
          flexWrap: 'wrap',
          marginBottom: 'var(--spacing-4)',
        }}
      >
        <div>
          <p
            style={{ margin: 0, color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}
          >
            {preset === 'TODAY'
              ? formatDateDisplay(range.startDate)
              : `${formatDateDisplay(range.startDate)} to ${formatDateDisplay(range.endDate)}`}
          </p>
          <p
            style={{
              margin: '4px 0 0',
              color: 'var(--color-text-muted)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            Weekly roster board with one row per barber and one day cell per shift plan.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
          {locationIdsInView.map((id) => {
            const palette = getPalette(id, locationIdsInView);
            return (
              <span
                key={id}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '6px 10px',
                  borderRadius: '999px',
                  backgroundColor: palette.card,
                  color: palette.text,
                  fontSize: '12px',
                  fontWeight: 600,
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '999px',
                    backgroundColor: palette.border,
                  }}
                />
                {locationNameById.get(id)}
              </span>
            );
          })}
        </div>
      </div>

      <div
        style={{
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-xl)',
          backgroundColor: '#fff',
          overflow: 'hidden',
          boxShadow: 'var(--shadow-sm)',
          display: isMobile ? 'none' : 'block',
        }}
      >
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: `${260 + days.length * 190}px` }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `260px repeat(${days.length}, minmax(190px, 1fr))`,
                borderBottom: '1px solid var(--color-border)',
                background:
                  'linear-gradient(180deg, rgba(249,245,242,0.85) 0%, rgba(255,255,255,1) 100%)',
              }}
            >
              <div
                style={{
                  padding: 'var(--spacing-4) var(--spacing-5)',
                  borderRight: '1px solid var(--color-border)',
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 'var(--font-size-base)' }}>Team</div>
                <div
                  style={{
                    color: 'var(--color-text-muted)',
                    fontSize: 'var(--font-size-sm)',
                    marginTop: '2px',
                  }}
                >
                  Barber and weekly coverage
                </div>
              </div>
              {days.map((day) => {
                const heading = formatDayHeader(day);
                const isToday = day === getTodayBrisbane();
                return (
                  <div
                    key={day}
                    style={{
                      padding: 'var(--spacing-4)',
                      borderRight: '1px solid var(--color-border)',
                      backgroundColor: isToday ? 'rgba(217,178,86,0.08)' : 'transparent',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '11px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        color: 'var(--color-text-muted)',
                        fontWeight: 700,
                      }}
                    >
                      {heading.weekday}
                    </div>
                    <div
                      style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, marginTop: '4px' }}
                    >
                      {heading.day}
                    </div>
                  </div>
                );
              })}
            </div>

            {barbersWithNoTradingShop.length > 0 && (
              <div
                style={{
                  padding: 'var(--spacing-3) var(--spacing-4)',
                  color: 'var(--color-text-muted)',
                  borderBottom: '1px solid var(--color-border)',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                {barbersWithNoTradingShop.join(', ')}{' '}
                {barbersWithNoTradingShop.length === 1 ? 'is' : 'are'} only assigned to a shop that
                has closed, so they have no section here. Add them to a trading location on the
                Barbers page to roster them.
              </div>
            )}

            {groupedBarbers.length === 0 && !loadingShifts && !loadingBreaks ? (
              <div style={{ padding: 'var(--spacing-8)', color: 'var(--color-text-muted)' }}>
                No barbers match the current filters.
              </div>
            ) : (
              groupedBarbers.map((group) => (
                <div key={group.locationId}>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: `260px repeat(${days.length}, minmax(190px, 1fr))`,
                      borderBottom: '1px solid var(--color-border)',
                      backgroundColor: 'var(--color-surface-elevated)',
                    }}
                  >
                    <div
                      style={{
                        padding: 'var(--spacing-3) var(--spacing-5)',
                        borderRight: '1px solid var(--color-border)',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 700,
                        color: 'var(--color-text)',
                      }}
                    >
                      {group.locationName}
                    </div>
                    <div
                      style={{
                        gridColumn: `span ${days.length}`,
                        padding: 'var(--spacing-3) var(--spacing-4)',
                        color: 'var(--color-text-muted)',
                        fontSize: 'var(--font-size-sm)',
                      }}
                    >
                      {group.barbers.length} {group.barbers.length === 1 ? 'barber' : 'barbers'}{' '}
                      scheduled in this section
                    </div>
                  </div>

                  {group.barbers.map((barber) => {
                    // Use the GROUP's location for palette + cell lookups,
                    // not `barber.location_id` — for multi-location barbers
                    // the home location wins both groups otherwise and the
                    // colour/data leaks across.
                    const palette = getPalette(group.locationId, locationIdsInView);
                    return (
                      <div
                        key={barber.id}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: `260px repeat(${days.length}, minmax(190px, 1fr))`,
                          borderBottom: '1px solid var(--color-border-light)',
                        }}
                      >
                        <div
                          style={{
                            padding: 'var(--spacing-4) var(--spacing-5)',
                            borderRight: '1px solid var(--color-border)',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            gap: '2px',
                            backgroundColor: '#fff',
                            position: 'sticky',
                            left: 0,
                            zIndex: 1,
                          }}
                        >
                          <div style={{ fontWeight: 700, color: 'var(--color-text)' }}>
                            {barber.name}
                          </div>
                          <div
                            style={{
                              fontSize: 'var(--font-size-sm)',
                              color: 'var(--color-text-muted)',
                            }}
                          >
                            {group.locationName}
                          </div>
                        </div>

                        {days.map((day) => {
                          const isToday = day === getTodayBrisbane();
                          const shiftsForCell =
                            shiftsByCell.get(getCellKey(day, barber.id, group.locationId)) || [];
                          const breaksForCell =
                            breaksByCell.get(getCellKey(day, barber.id, group.locationId)) || [];

                          return (
                            <div
                              key={`${barber.id}-${day}`}
                              style={{
                                minHeight: '136px',
                                padding: 'var(--spacing-3)',
                                borderRight: '1px solid var(--color-border-light)',
                                backgroundColor: isToday ? 'rgba(217,178,86,0.04)' : '#fff',
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 'var(--spacing-2)',
                                }}
                              >
                                {shiftsForCell.map((shift: any) => (
                                  <div
                                    key={shift.id}
                                    onClick={() => openEditShift(shift)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        openEditShift(shift);
                                      }
                                    }}
                                    style={{
                                      textAlign: 'left',
                                      border: `1px solid ${palette.border}33`,
                                      borderLeft: `4px solid ${palette.border}`,
                                      borderRadius: 'var(--radius-lg)',
                                      backgroundColor: palette.card,
                                      padding: 'var(--spacing-3)',
                                      boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontWeight: 700,
                                        color: palette.text,
                                        fontSize: 'var(--font-size-base)',
                                      }}
                                    >
                                      {formatBrisbaneTime(shift.start_time)} -{' '}
                                      {formatBrisbaneTime(shift.end_time)}
                                    </div>
                                    <div
                                      style={{
                                        marginTop: '2px',
                                        fontSize: 'var(--font-size-sm)',
                                        color: palette.muted,
                                      }}
                                    >
                                      Shift
                                    </div>
                                    {breaksForCell.length > 0 && (
                                      <div
                                        style={{
                                          display: 'flex',
                                          flexDirection: 'column',
                                          gap: '6px',
                                          marginTop: 'var(--spacing-3)',
                                        }}
                                      >
                                        {breaksForCell.map((item: any) => (
                                          <button
                                            key={item.id}
                                            type="button"
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              openEditBreak(item);
                                            }}
                                            style={{
                                              textAlign: 'left',
                                              border: '1px dashed rgba(199, 155, 45, 0.8)',
                                              borderRadius: 'var(--radius-default)',
                                              backgroundColor: 'rgba(246, 232, 188, 0.8)',
                                              padding: '8px 10px',
                                              color: '#6f5620',
                                            }}
                                          >
                                            <div style={{ fontWeight: 700, fontSize: '12px' }}>
                                              Break
                                            </div>
                                            <div style={{ fontSize: '12px', marginTop: '2px' }}>
                                              {formatBrisbaneTime(item.start_time)} -{' '}
                                              {formatBrisbaneTime(item.end_time)}
                                            </div>
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}

                                {shiftsForCell.length === 0 &&
                                  breaksForCell.map((item: any) => (
                                    <button
                                      key={item.id}
                                      type="button"
                                      onClick={() => openEditBreak(item)}
                                      style={{
                                        textAlign: 'left',
                                        border: '1px dashed rgba(199, 155, 45, 0.8)',
                                        borderRadius: 'var(--radius-lg)',
                                        backgroundColor: 'rgba(246, 232, 188, 0.72)',
                                        padding: 'var(--spacing-3)',
                                        color: '#6f5620',
                                      }}
                                    >
                                      <div style={{ fontWeight: 700, fontSize: '12px' }}>Break</div>
                                      <div style={{ fontSize: '12px', marginTop: '2px' }}>
                                        {formatBrisbaneTime(item.start_time)} -{' '}
                                        {formatBrisbaneTime(item.end_time)}
                                      </div>
                                    </button>
                                  ))}

                                {shiftsForCell.length === 0 && breaksForCell.length === 0 && (
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: 'var(--spacing-2)',
                                      alignItems: 'flex-start',
                                      justifyContent: 'center',
                                      minHeight: '100%',
                                    }}
                                  >
                                    <div
                                      style={{
                                        color: 'var(--color-text-muted)',
                                        fontSize: 'var(--font-size-sm)',
                                      }}
                                    >
                                      No roster set
                                    </div>
                                    <div
                                      style={{
                                        display: 'flex',
                                        gap: 'var(--spacing-2)',
                                        flexWrap: 'wrap',
                                      }}
                                    >
                                      <button
                                        type="button"
                                        onClick={() =>
                                          openNewShift(day, barber.id, group.locationId)
                                        }
                                        style={cellButtonStyle}
                                      >
                                        + Shift
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => openNewBreak(day, barber.id)}
                                        style={cellButtonStyle}
                                      >
                                        + Break
                                      </button>
                                    </div>
                                  </div>
                                )}

                                {shiftsForCell.length > 0 && (
                                  <div
                                    style={{
                                      display: 'flex',
                                      gap: 'var(--spacing-2)',
                                      flexWrap: 'wrap',
                                    }}
                                  >
                                    <button
                                      type="button"
                                      onClick={() => openNewShift(day, barber.id, group.locationId)}
                                      style={cellButtonStyle}
                                    >
                                      + Shift
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => openNewBreak(day, barber.id)}
                                      style={cellButtonStyle}
                                    >
                                      + Break
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {isMobile && (
        <div
          style={{
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-xl)',
            backgroundColor: '#fff',
            boxShadow: 'var(--shadow-sm)',
            overflow: 'hidden',
          }}
        >
          {/* Day navigator */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--spacing-3) var(--spacing-4)',
              borderBottom: '1px solid var(--color-border)',
              background:
                'linear-gradient(180deg, rgba(249,245,242,0.85) 0%, rgba(255,255,255,1) 100%)',
            }}
          >
            <button
              onClick={() => setMobileDayIndex((i) => Math.max(0, i - 1))}
              disabled={mobileDayIndex === 0}
              aria-label="Previous day"
              style={{
                ...btnStyle,
                minWidth: 44,
                minHeight: 44,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 'var(--font-size-xl)',
                opacity: mobileDayIndex === 0 ? 0.35 : 1,
              }}
            >
              &#8249;
            </button>
            <span style={{ fontWeight: 700, fontSize: 'var(--font-size-base)' }}>
              {days[mobileDayIndex] ? formatDayNav(days[mobileDayIndex]) : ''}
            </span>
            <button
              onClick={() => setMobileDayIndex((i) => Math.min(days.length - 1, i + 1))}
              disabled={mobileDayIndex >= days.length - 1}
              aria-label="Next day"
              style={{
                ...btnStyle,
                minWidth: 44,
                minHeight: 44,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 'var(--font-size-xl)',
                opacity: mobileDayIndex >= days.length - 1 ? 0.35 : 1,
              }}
            >
              &#8250;
            </button>
          </div>

          {/* Barber cards for selected day */}
          {groupedBarbers.length === 0 && !loadingShifts && !loadingBreaks ? (
            <div
              style={{
                padding: 'var(--spacing-8)',
                color: 'var(--color-text-muted)',
                textAlign: 'center',
              }}
            >
              No barbers match the current filters.
            </div>
          ) : (
            <div
              style={{
                padding: 'var(--spacing-3)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--spacing-4)',
              }}
            >
              {groupedBarbers.map((group) => {
                const groupPalette = getPalette(group.locationId, locationIdsInView);
                return (
                  <div key={group.locationId}>
                    <div
                      style={{
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 700,
                        color: groupPalette.text,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        padding: 'var(--spacing-2) 0',
                        marginBottom: 'var(--spacing-2)',
                      }}
                    >
                      {group.locationName}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'var(--spacing-3)',
                      }}
                    >
                      {group.barbers.map((barber) => {
                        const day = days[mobileDayIndex];
                        if (!day) return null;
                        const palette = getPalette(group.locationId, locationIdsInView);
                        const shiftsForCell =
                          shiftsByCell.get(getCellKey(day, barber.id, group.locationId)) || [];
                        const breaksForCell =
                          breaksByCell.get(getCellKey(day, barber.id, group.locationId)) || [];
                        return (
                          <div
                            key={barber.id}
                            style={{
                              border: `1px solid ${palette.border}33`,
                              borderLeft: `4px solid ${palette.border}`,
                              borderRadius: 'var(--radius-lg)',
                              backgroundColor: palette.card,
                              padding: 'var(--spacing-4)',
                            }}
                          >
                            <div
                              style={{
                                fontWeight: 700,
                                color: palette.text,
                                fontSize: 'var(--font-size-base)',
                                marginBottom: '2px',
                              }}
                            >
                              {barber.name}
                            </div>
                            <div
                              style={{
                                fontSize: 'var(--font-size-sm)',
                                color: palette.muted,
                                marginBottom: 'var(--spacing-3)',
                              }}
                            >
                              {group.locationName}
                            </div>

                            {shiftsForCell.length === 0 && breaksForCell.length === 0 ? (
                              <div
                                style={{
                                  fontSize: 'var(--font-size-sm)',
                                  color: 'var(--color-text-muted)',
                                  marginBottom: 'var(--spacing-3)',
                                }}
                              >
                                No shift
                              </div>
                            ) : (
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 'var(--spacing-2)',
                                  marginBottom: 'var(--spacing-3)',
                                }}
                              >
                                {shiftsForCell.map((shift: any) => (
                                  <button
                                    key={shift.id}
                                    type="button"
                                    onClick={() => openEditShift(shift)}
                                    style={{
                                      textAlign: 'left',
                                      border: `1px solid ${palette.border}33`,
                                      borderRadius: 'var(--radius-default)',
                                      backgroundColor: '#fff',
                                      padding: 'var(--spacing-3)',
                                      minHeight: 44,
                                      cursor: 'pointer',
                                      width: '100%',
                                    }}
                                  >
                                    <div style={{ fontWeight: 700, color: palette.text }}>
                                      {formatBrisbaneTime(shift.start_time)} –{' '}
                                      {formatBrisbaneTime(shift.end_time)}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 'var(--font-size-sm)',
                                        color: palette.muted,
                                      }}
                                    >
                                      Shift
                                    </div>
                                  </button>
                                ))}
                                {breaksForCell.map((item: any) => (
                                  <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => openEditBreak(item)}
                                    style={{
                                      textAlign: 'left',
                                      border: '1px dashed rgba(199,155,45,0.8)',
                                      borderRadius: 'var(--radius-default)',
                                      backgroundColor: 'rgba(246,232,188,0.8)',
                                      padding: 'var(--spacing-3)',
                                      minHeight: 44,
                                      cursor: 'pointer',
                                      color: '#6f5620',
                                      width: '100%',
                                    }}
                                  >
                                    <div style={{ fontWeight: 700, fontSize: '12px' }}>Break</div>
                                    <div style={{ fontSize: '12px', marginTop: '2px' }}>
                                      {formatBrisbaneTime(item.start_time)} –{' '}
                                      {formatBrisbaneTime(item.end_time)}
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}

                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 'var(--spacing-2)',
                              }}
                            >
                              <button
                                type="button"
                                onClick={() => openNewShift(day, barber.id, group.locationId)}
                                style={{
                                  ...btnAccent,
                                  width: '100%',
                                  minHeight: 44,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                + Shift
                              </button>
                              <button
                                type="button"
                                onClick={() => openNewBreak(day, barber.id)}
                                style={{
                                  ...btnStyle,
                                  width: '100%',
                                  minHeight: 44,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                + Break
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {(loadingShifts || loadingBreaks) && (
        <p style={{ color: 'var(--color-text-muted)', marginTop: 'var(--spacing-3)' }}>
          Loading roster...
        </p>
      )}

      {shiftForm &&
        (() => {
          // Build time options (:00 and :30 only) within opening hours for the selected location/day
          const selectedDow = shiftForm.date ? new Date(shiftForm.date + 'T12:00:00').getDay() : -1;
          const locHours = openingHours.find(
            (h: any) => h.location_id === shiftForm.location_id && h.day_of_week === selectedDow,
          );
          const openTime = locHours?.open_time || '07:00';
          const closeTime = locHours?.close_time || '18:00';
          const toMinutes = (t: string) => {
            const [h, m] = t.split(':').map(Number);
            return h * 60 + (m || 0);
          };
          const formatTime = (mins: number) => {
            const h = String(Math.floor(mins / 60)).padStart(2, '0');
            const m = String(mins % 60).padStart(2, '0');
            return `${h}:${m}`;
          };
          const formatTimeLabel = (mins: number) => {
            const h24 = Math.floor(mins / 60);
            const m = mins % 60;
            const ampm = h24 >= 12 ? 'PM' : 'AM';
            const h12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
            return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
          };
          const openMin = toMinutes(openTime);
          const closeMin = toMinutes(closeTime);
          const timeOptions: { value: string; label: string }[] = [];
          for (let m = openMin; m <= closeMin; m += 30) {
            timeOptions.push({ value: formatTime(m), label: formatTimeLabel(m) });
          }

          return (
            <ModalShell
              title={shiftForm.id ? 'Edit Shift' : 'Add Shift'}
              onClose={() => setShiftForm(null)}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
                  gap: 'var(--spacing-3)',
                }}
              >
                <div>
                  <label style={labelStyle}>Barber</label>
                  <select
                    value={shiftForm.barber_id}
                    onChange={(e) =>
                      setShiftForm((current) =>
                        current ? { ...current, barber_id: Number(e.target.value) } : current,
                      )
                    }
                    style={inputStyle}
                  >
                    {filteredBarbers.map((barber: any) => (
                      <option key={barber.id} value={barber.id}>
                        {barber.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Location</label>
                  <select
                    value={shiftForm.location_id}
                    onChange={(e) =>
                      setShiftForm((current) =>
                        current ? { ...current, location_id: Number(e.target.value) } : current,
                      )
                    }
                    style={inputStyle}
                  >
                    {locations.map((location: any) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Date</label>
                  <input
                    type="date"
                    value={shiftForm.date}
                    onChange={(e) =>
                      setShiftForm((current) =>
                        current ? { ...current, date: e.target.value } : current,
                      )
                    }
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Start</label>
                  <select
                    value={shiftForm.startTime}
                    onChange={(e) =>
                      setShiftForm((current) =>
                        current ? { ...current, startTime: e.target.value } : current,
                      )
                    }
                    style={inputStyle}
                  >
                    {timeOptions
                      .filter((t) => t.value < (shiftForm.endTime || closeTime))
                      .map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>End</label>
                  <select
                    value={shiftForm.endTime}
                    onChange={(e) =>
                      setShiftForm((current) =>
                        current ? { ...current, endTime: e.target.value } : current,
                      )
                    }
                    style={inputStyle}
                  >
                    {timeOptions
                      .filter((t) => t.value > (shiftForm.startTime || openTime))
                      .map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: 'var(--spacing-2)',
                  marginTop: 'var(--spacing-4)',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  {shiftForm.id && (
                    <button
                      onClick={() => deleteShiftMutation.mutate(shiftForm.id!)}
                      style={{
                        ...btnStyle,
                        borderColor: 'rgba(220,53,69,0.25)',
                        color: 'var(--color-error)',
                      }}
                    >
                      Delete Shift
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                  <button onClick={() => setShiftForm(null)} style={btnStyle}>
                    Cancel
                  </button>
                  <button onClick={saveShift} style={btnAccent}>
                    {createShiftMutation.isPending || updateShiftMutation.isPending
                      ? 'Saving...'
                      : shiftForm.id
                        ? 'Update Shift'
                        : 'Add Shift'}
                  </button>
                </div>
              </div>
            </ModalShell>
          );
        })()}

      {breakForm && (
        <ModalShell
          title={breakForm.id ? 'Edit Break' : 'Add Break'}
          onClose={() => setBreakForm(null)}
        >
          <div
            className="resp-2col"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
              gap: 'var(--spacing-3)',
            }}
          >
            <div>
              <label style={labelStyle}>Barber</label>
              <select
                value={breakForm.barber_id}
                onChange={(e) =>
                  setBreakForm((current) =>
                    current ? { ...current, barber_id: Number(e.target.value) } : current,
                  )
                }
                style={inputStyle}
              >
                {filteredBarbers.map((barber: any) => (
                  <option key={barber.id} value={barber.id}>
                    {barber.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Date</label>
              <input
                type="date"
                value={breakForm.date}
                onChange={(e) =>
                  setBreakForm((current) =>
                    current ? { ...current, date: e.target.value } : current,
                  )
                }
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Start</label>
              <input
                type="time"
                value={breakForm.startTime}
                onChange={(e) =>
                  setBreakForm((current) =>
                    current ? { ...current, startTime: e.target.value } : current,
                  )
                }
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>End</label>
              <input
                type="time"
                value={breakForm.endTime}
                onChange={(e) =>
                  setBreakForm((current) =>
                    current ? { ...current, endTime: e.target.value } : current,
                  )
                }
                style={inputStyle}
              />
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              gap: 'var(--spacing-2)',
              marginTop: 'var(--spacing-4)',
              justifyContent: 'space-between',
            }}
          >
            <div>
              {breakForm.id && (
                <button
                  onClick={() => deleteBreakMutation.mutate(breakForm.id!)}
                  style={{
                    ...btnStyle,
                    borderColor: 'rgba(220,53,69,0.25)',
                    color: 'var(--color-error)',
                  }}
                >
                  Delete Break
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
              <button onClick={() => setBreakForm(null)} style={btnStyle}>
                Cancel
              </button>
              <button onClick={saveBreak} style={btnAccent}>
                {createBreakMutation.isPending || updateBreakMutation.isPending
                  ? 'Saving...'
                  : breakForm.id
                    ? 'Update Break'
                    : 'Add Break'}
              </button>
            </div>
          </div>
        </ModalShell>
      )}

      {copyDialogOpen && (
        <ModalShell title="Copy a past week" onClose={() => setCopyDialogOpen(false)}>
          <div style={{ marginBottom: 'var(--spacing-3)', color: 'var(--color-text-muted)' }}>
            Copy a past week's roster into{' '}
            <strong style={{ color: 'var(--color-text)' }}>
              {formatDateDisplay(range.startDate)}
            </strong>
            . Only shifts + regular breaks are copied. Catchups stay on the Calendar.
          </div>

          {/* Which past week to copy FROM — critical for fortnightly patterns
              like Lee's alternating Thursday shift. Default 1 week ago is the
              classic case; 2 weeks preserves fortnightly rotations. */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-3)',
              marginBottom: 'var(--spacing-3)',
              padding: 'var(--spacing-3)',
              backgroundColor: 'var(--color-surface-elevated)',
              borderRadius: 'var(--radius-default)',
            }}
          >
            <label
              htmlFor="copy-weeks-back"
              style={{
                ...labelStyle,
                marginBottom: 0,
                textTransform: 'none',
                letterSpacing: 'normal',
                fontSize: 'var(--font-size-sm)',
                whiteSpace: 'nowrap',
              }}
            >
              Copy from
            </label>
            <select
              id="copy-weeks-back"
              value={copyWeeksBack}
              onChange={(e) => setCopyWeeksBack(Number(e.target.value))}
              style={{ ...selectStyle, flex: '0 0 auto' }}
            >
              <option value={1}>1 week ago (previous week)</option>
              <option value={2}>2 weeks ago (fortnightly patterns)</option>
              <option value={3}>3 weeks ago</option>
              <option value={4}>4 weeks ago</option>
            </select>
            <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
              {formatDateDisplay(addDays(range.startDate, -7 * copyWeeksBack))}
            </span>
          </div>

          {/* Master toggle — select/clear all filtered barbers */}
          <div
            style={{
              display: 'flex',
              gap: 'var(--spacing-2)',
              marginBottom: 'var(--spacing-3)',
              paddingBottom: 'var(--spacing-2)',
              borderBottom: '1px solid var(--color-border)',
            }}
          >
            <button
              type="button"
              style={btnStyle}
              onClick={() => setCopyDialogSelection(new Set(filteredBarbers.map((b: any) => b.id)))}
            >
              Select all
            </button>
            <button
              type="button"
              style={btnStyle}
              onClick={() => setCopyDialogSelection(new Set())}
            >
              Clear
            </button>
            <span
              style={{
                marginLeft: 'auto',
                alignSelf: 'center',
                color: 'var(--color-text-muted)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              {copyDialogSelection.size} of {filteredBarbers.length} selected
            </span>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: 'var(--spacing-2)',
              maxHeight: '360px',
              overflowY: 'auto',
            }}
          >
            {filteredBarbers.map((barber: any) => {
              const checked = copyDialogSelection.has(barber.id);
              const locLabel = Array.isArray(barber.locations)
                ? barber.locations.map((l: any) => l.name).join(' + ')
                : locationNameById.get(barber.location_id) || '';
              return (
                <label
                  key={barber.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-2)',
                    padding: 'var(--spacing-2) var(--spacing-3)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-default)',
                    cursor: 'pointer',
                    backgroundColor: checked ? 'rgba(217,178,86,0.10)' : 'transparent',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => {
                      setCopyDialogSelection((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(barber.id);
                        else next.delete(barber.id);
                        return next;
                      });
                    }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontWeight: 600 }}>{barber.name}</span>
                    <span
                      style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}
                    >
                      {locLabel}
                    </span>
                  </div>
                </label>
              );
            })}
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 'var(--spacing-2)',
              marginTop: 'var(--spacing-4)',
            }}
          >
            <button style={btnStyle} onClick={() => setCopyDialogOpen(false)}>
              Cancel
            </button>
            <button
              style={btnAccent}
              disabled={copyDialogSelection.size === 0 || copyWeekMutation.isPending}
              onClick={() => {
                // Build targets from the SELECTED barbers only, sourcing
                // from copyWeeksBack weeks ago. Per-location fan-out +
                // handleBreaks-once-per-barber logic unchanged.
                const sourceWeekStart = addDays(range.startDate, -7 * copyWeeksBack);
                const targets: {
                  barberId: number;
                  locationId: number;
                  weekStart: string;
                  handleBreaks: boolean;
                  sourceWeekStart: string;
                }[] = [];
                const breaksClaimedByBarber = new Set<number>();
                for (const group of groupedBarbers) {
                  for (const barber of group.barbers) {
                    if (!copyDialogSelection.has(barber.id)) continue;
                    const handleBreaks = !breaksClaimedByBarber.has(barber.id);
                    breaksClaimedByBarber.add(barber.id);
                    targets.push({
                      barberId: barber.id,
                      locationId: group.locationId,
                      weekStart: range.startDate,
                      handleBreaks,
                      sourceWeekStart,
                    });
                  }
                }
                if (targets.length === 0) return;
                setCopyDialogOpen(false);
                copyWeekMutation.mutate(targets);
              }}
            >
              {copyWeekMutation.isPending
                ? 'Copying…'
                : `Copy ${copyDialogSelection.size} barber${copyDialogSelection.size === 1 ? '' : 's'}`}
            </button>
          </div>
        </ModalShell>
      )}

      {/* Toast notification */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '10px 20px',
            borderRadius: 'var(--radius-default)',
            backgroundColor: toast.type === 'success' ? '#16a34a' : '#dc3545',
            color: '#fff',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
            zIndex: 2000,
            whiteSpace: 'nowrap',
            animation: 'fadeInUp 0.25s ease-out',
          }}
        >
          {toast.type === 'success' ? '✓' : '✕'} {toast.message}
        </div>
      )}
    </div>
  );
}
